// src/lib/observability/sentry-filters.ts
import type * as Sentry from "@sentry/nextjs";

export function dropNetworkAbort(event: Sentry.Event, hint?: Sentry.EventHint) {
  const err = hint?.originalException as any;
  const msg = (err?.message || event?.message || "").toString().toLowerCase();
  const code = String(err?.code || "").toUpperCase();
  const name = String(err?.name || "");

  // Common noisy aborts we don't want
  if (
    code === "ECONNRESET" ||
    msg === "aborted" ||
    msg.includes("client closed request") ||
    name === "AbortError"
  ) {
    return null;
  }
  return event;
}

const REDACTED = "[redacted]";
const SENSITIVE_KEYS = new Set([
  "doi",
  "pmid",
  "pmcid",
  "isbn",
  "issn",
  "eissn",
  "token",
  "bibcode",
  "raw",
  "url",
  "key",
  "cache_key",
  "text",
  "input",
  "line",
  "lines",
  "query",
]);

// `seen` holds the current ancestor chain only — entries are removed on the way
// back out. Without the guard a self-referential object recurses until the stack
// blows (RangeError). The `beforeSend` path is shielded by Sentry's own
// normalize (normalizeDepth 3, cycles -> "[Circular ~]"), but `beforeBreadcrumb`
// runs on the raw breadcrumb before any normalization, and its consoleSandbox
// wrapper is try/finally — so a throw here would propagate into the caller of
// Sentry.addBreadcrumb(). Deleting on exit matters: a permanent WeakSet would
// flag merely-shared (diamond) subobjects as cycles and redact them wholesale.
function redactObject(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return REDACTED;
  seen.add(value);

  try {
    if (Array.isArray(value)) return value.map((item) => redactObject(item, seen));

    const obj = value as Record<string, unknown>;
    for (const [k, v] of Object.entries(obj)) {
      if (SENSITIVE_KEYS.has(k.toLowerCase())) {
        obj[k] = REDACTED;
        continue;
      }
      obj[k] = redactObject(v, seen);
    }
    return obj;
  } finally {
    seen.delete(value);
  }
}

const QUERYLIKE_RE =
  /\b(?:doi|pmid|pmcid|isbn|issn|eissn|arxiv|bibcode|token|id|url)\s*[:=]\s*[^\s,;]+/gi;
const DOIISH_RE = /\b10\.\d{4,9}\/[^\s"'<>]+/gi;

function scrubText(value: string): string {
  return value.replace(QUERYLIKE_RE, REDACTED).replace(DOIISH_RE, REDACTED);
}

function stripQuery(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.search = "";
    return parsed.toString();
  } catch {
    return url.split("?")[0] || url;
  }
}

export function scrubSensitiveBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  const cat = String(breadcrumb.category ?? "").toLowerCase();
  if (cat === "console") return null;

  if (breadcrumb.data) {
    redactObject(breadcrumb.data as Record<string, unknown>);
    const data = breadcrumb.data as Record<string, unknown>;
    if (typeof data.url === "string") data.url = stripQuery(scrubText(data.url));
  }
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = scrubText(breadcrumb.message).replace(/\?.*$/, "");
  }
  return breadcrumb;
}

export function scrubSensitiveTelemetry(event: Sentry.Event): Sentry.Event {
  if (event.extra) redactObject(event.extra);
  if (event.contexts) redactObject(event.contexts as unknown as Record<string, unknown>);
  if (typeof event.message === "string") event.message = scrubText(event.message);

  if (event.request?.url) {
    event.request.url = stripQuery(event.request.url);
  }
  if (typeof event.request?.query_string === "string") {
    event.request.query_string = scrubText(event.request.query_string);
  }
  if (event.request?.data) {
    if (typeof event.request.data === "string") {
      event.request.data = scrubText(event.request.data);
    } else {
      redactObject(event.request.data as Record<string, unknown>);
    }
  }
  if (event.request?.headers) {
    redactObject(event.request.headers as unknown as Record<string, unknown>);
  }
  const values = event.exception?.values;
  if (values?.length) {
    for (const ex of values) {
      if (typeof ex.value === "string") ex.value = scrubText(ex.value);
      if (typeof ex.type === "string") ex.type = scrubText(ex.type);
    }
  }
  if (event.breadcrumbs?.length) {
    event.breadcrumbs = event.breadcrumbs
      .map((crumb) => scrubSensitiveBreadcrumb(crumb))
      .filter(Boolean) as Sentry.Breadcrumb[];
  }
  return event;
}
